import _ from 'lodash'
import PStorage from '@/util/PStorage'
import Axios, { AxiosRequestConfig, AxiosInstance } from 'axios'
import { toCamelCase, toSnakeCase } from '@/util/api/Case'
import { Dictionary, Maybe } from '@/types/general'
import NPMPackage from '@/../package.json'
import { isElectron } from '../Helpers'

const BASE_URL: string = window.location.host === 'pass.matho.me' || !window.location.host.includes('localhost')
    ? 'https://api.pass.matho.me/v1/'
    : 'http://localhost/v1/'

const DEBUG = true

const APP_VERSION = `${(isElectron() ? 'electron' : 'web')}-${NPMPackage.version}`

/**
 * A wrapper for the Axios API interface
 *
 * Adds current authentication headers and extracts data from each request
 */
class API {
    private axios: AxiosInstance
    private connected: boolean = false
    private whenConnectedCallbacks: (() => any)[] = []

    constructor() {
        this.axios = Axios.create({
            baseURL: BASE_URL,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'App-Version': APP_VERSION
            }
        })
    }

    /**
     * Get the request config with the current API token attached
     */
    private config(): AxiosRequestConfig {
        return {
            headers: {
                Authorization: `Bearer ${PStorage.get('api_token')}`
            }
        }
    }

    /**
     * Queue a callback to be run when the app is
     * connected to the server and authenticated.
     *
     * Runs the callback immediately if already connected
     */
    public whenConnected(callback: () => any): void {
        if (this.connected) {
            setTimeout(callback)
        } else {
            this.whenConnectedCallbacks.push(callback)
        }
    }

    /**
     * Mark the API as connected / not connected
     */
    public setConnected(value: boolean): void {
        this.connected = value
        if (value) {
            this.runConnectedCallbacks()
        }
    }

    /**
     * Execute all waiting callbacks when we are
     * connected and authenticated
     */
    private runConnectedCallbacks(): void {
        setTimeout(() => {
            this.whenConnectedCallbacks.forEach(callback => callback())
            this.whenConnectedCallbacks = []
        })
    }

    /**
     * Make an authenticated request with _func_ and return the
     * resulting data
     *
     * TODO: return data, status, error but catch 500 errors
     *
     * @param func axios request function
     * @param endpoint URL
     * @param data POST/PUT/etc data
     * @param auth whether to send authentication header
     * @returns response data
     */
    private async request(
        func: Function, endpoint: string,
        data?: any, auth: boolean = true
    ): Promise<any> {
        if (endpoint[0] !== '/') {
            endpoint = `/${endpoint}`
        }
        if (DEBUG) {
            console.debug(
                `%c< REQUEST%c${endpoint}`,
                'background:#314052;padding:1px 6px;border-radius:3px 0 0 3px;color:white;border:1px solid #314052',
                'padding:1px 6px;border-radius:0 3px 3px 0;color:#314052;border:1px solid #314052',
                toSnakeCase(data) || ''
            )
        }
        try {
            const config: AxiosRequestConfig = auth ? this.config() : {}
            const result = data === undefined
                ? await func(endpoint, config)
                : await func(endpoint, toSnakeCase(data), config)
            if (DEBUG) {
                console.debug(
                    `%c> RESPONSE%c${endpoint}`,
                    'background:#8dc63e;padding:1px 6px;border-radius:3px 0 0 3px;color:white;border:1px solid #8dc63e',
                    'padding:1px 6px;border-radius:0 3px 3px 0;color:#314052;border:1px solid #8dc63e',
                    toCamelCase(result) || ''
                )
            }

            // on first successful authenticated request,
            // mark the api as connected and run callbacks
            if (auth && !this.connected) {
                this.setConnected(true)
            }
            return toCamelCase(result.data)
        } catch (err) {
            // mark api as not connected if we get
            // an unauthorised error back
            if (err.response && err.response.status === 401) {
                this.setConnected(false)
            }
            // unknown error
            console.debug(
                `%c> ERROR RESPONSE%c${endpoint}`,
                'background:red;padding:1px 6px;border-radius:3px 0 0 3px;color:white;border:1px solid #8dc63e',
                'padding:1px 6px;border-radius:0 3px 3px 0;color:#314052;border:1px solid #8dc63e',
                toCamelCase(err) || ''
            )
            throw err
        }
    }

    /**
     * GET the URL
     *
     * @param endpoint URL
     */
    public async get(endpoint: string, data?: Dictionary<Maybe<string>>): Promise<any> {
        const query = (data ? Object.keys(data) : []).reduce((qs, key) => {
            if (data![key] !== undefined) {
                return `${qs}${qs ? '&' : '?'}${_.snakeCase(key)}=${data![key]}`
            }
            return qs
        }, '')
        return this.request(this.axios.get, endpoint + query)
    }

    /**
     * POST to the URL
     *
     * @param endpoint URL
     * @param data POST data
     * @param auth whether to send authentication header
     */
    public async post(endpoint: string, data?: any, auth: boolean = true): Promise<any> {
        return this.request(this.axios.post, endpoint, data, auth)
    }

    /**
     * PUT to the URL
     *
     * @param endpoint URL
     * @param data PUT data
     */
    public async put(endpoint: string, data?: any): Promise<any> {
        return this.request(this.axios.put, endpoint, data)
    }

    /**
     * DELETE the URL
     *
     * @param endpoint URL
     * @param data DELETE data
     */
    public async delete(endpoint: string, data?: any): Promise<any> {
        return this.request(this.axios.delete, endpoint, data)
    }
}

export default new API()
